import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { formatBusinessDate } from '../utils/casinoDay';
import {
  X,
  User,
  CreditCard,
  Star,
  Clock,
  Gift,
  MapPin,
  Gamepad2,
  Award,
  TrendingUp,
  Link2,
  Link2Off,
  RefreshCw,
  Cake,
  AlertCircle,
  FileText,
  Crown,
  Lock,
  Mail,
  Phone,
  Calendar,
} from 'lucide-react';

// Tier colors matching the mobile app (supports both TableCaptain and App tier names)
export const TIER_COLORS = {
  // App tier names
  Chairman: '#B9F2FF',     // Diamond/Cyan (top tier)
  Boardmember: '#B9F2FF',  // Diamond/Cyan
  Ambassador: '#FFD700',   // Gold
  Elite: '#C0C0C0',        // Silver
  VIP: '#CD7F32',          // Bronze
  // TableCaptain tier names (legacy)
  Legend: '#B9F2FF',       // Diamond/Cyan
  "Chairman's Circle": '#E5E4E2',  // Platinum
};

// Helper function to get tier color from display name (e.g., "Boardmember II" -> Diamond color)
export const getTierColorFromDisplay = (tierDisplay) => {
  if (!tierDisplay) return TIER_COLORS.VIP;
  const tierStr = tierDisplay.toString();
  // Extract base tier name (remove level suffix like " I", " II", " III")
  const baseTier = tierStr.replace(/\s+(I{1,3}|IV|V)$/i, '').trim();
  return TIER_COLORS[baseTier] || TIER_COLORS.VIP;
};

// Map Ionicon names to emojis for achievement display
const ICON_MAP = {
  'footsteps': '\u{1F463}',
  'calendar': '\u{1F4C5}',
  'calendar-outline': '\u{1F5D3}️',
  'star': '⭐',
  'star-outline': '⭐',
  'time': '⏱️',
  'time-outline': '⏰',
  'trophy': '\u{1F3C6}',
  'trophy-outline': '\u{1F3C6}',
  'medal': '\u{1F947}',
  'medal-outline': '\u{1F3C5}',
  'ribbon': '\u{1F380}',
  'flame': '\u{1F525}',
  'flash': '⚡',
  'diamond': '\u{1F48E}',
  'diamond-outline': '\u{1F48E}',
  'grid': '\u{1F3B0}',
  'grid-outline': '\u{1F3B2}',
  'apps': '\u{1F4F1}',
  'apps-outline': '\u{1F4F1}',
  'cash': '\u{1F4B5}',
  'cash-outline': '\u{1F4B0}',
  'card': '\u{1F0CF}',
  'card-outline': '\u{1F3B4}',
  'heart': '❤️',
  'sparkles': '✨',
  'rocket': '\u{1F680}',
  'crown': '\u{1F451}',
  'shield': '\u{1F6E1}️',
  'shield-checkmark': '\u{1F6E1}️',
  'timer': '⏲️',
  'timer-outline': '⏲️',
  'hourglass': '⏳',
  'hourglass-outline': '⏳',
  'checkmark-circle': '✅',
  'checkmark-circle-outline': '✅',
  'people': '\u{1F465}',
  'people-outline': '\u{1F465}',
  'game-controller': '\u{1F3AE}',
  'game-controller-outline': '\u{1F3AE}',
};

const getAchievementIcon = (iconName) => {
  return ICON_MAP[iconName] || ICON_MAP[iconName?.replace('-outline', '')] || '\u{1F3C6}';
};

// Tier Badge Component
export const TierBadge = ({ tier, color, small = false }) => (
  <span
    className={`inline-flex items-center max-w-full px-2 py-0.5 rounded-md text-xs font-medium border ${small ? 'text-[10px]' : ''}`}
    style={{
      backgroundColor: color + '20',
      borderColor: color,
      color: color,
    }}
    title={typeof tier === 'string' ? tier : undefined}
  >
    <span className="truncate">{tier}</span>
  </span>
);

// Guest badge (neutral gray) for players with no tier
const GuestBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium border border-gray-500 text-gray-400 bg-gray-500/10">Guest</span>
);

// Player Detail Modal Component (shared)
const PlayerDetailModal = ({ visible, onClose, playerId, player, appUserId: appUserIdProp, playerClubMember }) => {
  const { can } = useAuth();
  const canContact = can('users:view_contact');
  const canIdentity = can('users:view_identity');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // VIP arrival alert toggle - null means "use the value from stats".
  const [vipAlert, setVipAlert] = useState(null);
  const [achievements, setAchievements] = useState(null);
  const [achievementsLoading, setAchievementsLoading] = useState(false);
  const [calculatedStatus, setCalculatedStatus] = useState(null);

  // Rewards state
  const [userRewards, setUserRewards] = useState(null);
  const [rewardsLoading, setRewardsLoading] = useState(false);
  const [rewardsTab, setRewardsTab] = useState('available'); // 'available' | 'history'
  const [redeemModal, setRedeemModal] = useState({ open: false, reward: null });
  const [redeemForm, setRedeemForm] = useState({ employeeNumber: '', pin: '', buyInAmount: '' });
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [redeemError, setRedeemError] = useState('');
  const [calculatedLoading, setCalculatedLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Tier Override state
  const [tierOverride, setTierOverride] = useState(null);
  const [tierOverrideLoading, setTierOverrideLoading] = useState(false);
  const [showOverrideForm, setShowOverrideForm] = useState(false);
  const [overrideForm, setOverrideForm] = useState({ tier: '', level: '', reason: '' });
  const [tierConfig, setTierConfig] = useState(null);
  const [overrideSaving, setOverrideSaving] = useState(false);

  // Derived identifiers so the modal works whether opened with appUserId+playerClubMember
  // (User Manager style) OR just a playerId (Reports/TableManager style).
  const tcPlayerId = playerClubMember?.tablecaptainPlayerId ?? playerId;
  const appUserId = appUserIdProp ?? stats?.appUser?.id ?? null;

  const fetchCalculatedStatus = useCallback(async () => {
    if (!appUserId) return;

    setCalculatedLoading(true);
    try {
      const response = await api.get(`/admin/users/${appUserId}/calculated-status`);
      setCalculatedStatus(response.data);
    } catch (err) {
      console.error('Error fetching calculated status:', err);
    } finally {
      setCalculatedLoading(false);
    }
  }, [appUserId]);

  // Fetch tier override status
  const fetchTierOverride = useCallback(async () => {
    if (!appUserId) return;
    setTierOverrideLoading(true);
    try {
      const [overrideRes, configRes] = await Promise.all([
        api.get(`/status-system/admin/override/${appUserId}`),
        api.get('/status-system/config'),
      ]);
      setTierOverride(overrideRes.data);
      setTierConfig(configRes.data);
    } catch (err) {
      console.error('Error fetching tier override:', err);
    } finally {
      setTierOverrideLoading(false);
    }
  }, [appUserId]);

  // Set tier override
  const handleSetOverride = async () => {
    if (!overrideForm.tier || !overrideForm.level) return;
    setOverrideSaving(true);
    try {
      await api.post(`/status-system/admin/override/${appUserId}`, {
        tier: overrideForm.tier,
        level: parseInt(overrideForm.level),
        reason: overrideForm.reason || undefined,
      });
      setShowOverrideForm(false);
      setOverrideForm({ tier: '', level: '', reason: '' });
      fetchTierOverride();
      fetchCalculatedStatus();
    } catch (err) {
      console.error('Error setting tier override:', err);
      alert(err.response?.data?.error || 'Failed to set tier override');
    } finally {
      setOverrideSaving(false);
    }
  };

  // Clear tier override
  const handleClearOverride = async () => {
    if (!confirm('Clear this tier override? The user will return to their earned tier.')) return;
    setOverrideSaving(true);
    try {
      await api.delete(`/status-system/admin/override/${appUserId}`);
      setShowOverrideForm(false);
      fetchTierOverride();
      fetchCalculatedStatus();
    } catch (err) {
      console.error('Error clearing tier override:', err);
      alert('Failed to clear tier override');
    } finally {
      setOverrideSaving(false);
    }
  };

  // Get available levels for selected tier
  const getAvailableLevels = (tierKey) => {
    if (!tierConfig?.tiers) return [];
    const tier = tierConfig.tiers.find(t => t.key === tierKey);
    return tier?.levels || [];
  };

  const handleRefreshStatus = async () => {
    if (!appUserId) return;

    setRefreshing(true);
    try {
      const response = await api.post(`/admin/users/${appUserId}/refresh-status`);
      setCalculatedStatus({
        ...calculatedStatus,
        calculated: {
          currentPeriodXp: response.data.updated.currentPeriodXp,
          lifetimeXp: response.data.updated.lifetimeXp,
          effectiveStatus: response.data.status.effectiveStatus,
        },
        stored: response.data.updated,
      });
      // Update the parent's view of playerClubMember
      if (playerClubMember) {
        playerClubMember.currentPeriodXp = response.data.updated.currentPeriodXp;
        playerClubMember.lifetimeXp = response.data.updated.lifetimeXp;
        playerClubMember.currentStatusDisplay = response.data.updated.currentStatusDisplay;
      }
    } catch (err) {
      console.error('Error refreshing status:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const fetchPlayerAchievements = useCallback(async () => {
    if (!appUserId) return;

    setAchievementsLoading(true);
    try {
      const response = await api.get(`/admin/reports/player-achievements/${appUserId}`);
      setAchievements(response.data);
    } catch (err) {
      console.error('Error fetching achievements:', err);
    } finally {
      setAchievementsLoading(false);
    }
  }, [appUserId]);

  const fetchUserRewards = useCallback(async () => {
    if (!appUserId) return;
    setRewardsLoading(true);
    try {
      const response = await api.get(`/admin/users/${appUserId}/rewards`);
      setUserRewards(response.data);
    } catch (err) {
      console.error('Error fetching user rewards:', err);
      setUserRewards({ rewards: [], redemptions: [], totalRedemptions: 0 });
    } finally {
      setRewardsLoading(false);
    }
  }, [appUserId]);

  const handleRedeemReward = async () => {
    if (!redeemModal.reward || !redeemForm.employeeNumber || !redeemForm.pin) return;
    setRedeemLoading(true);
    setRedeemError('');
    try {
      const payload = {
        employeeNumber: redeemForm.employeeNumber,
        pin: redeemForm.pin,
      };
      // Buy-in is only captured for percent (promotional buy-in bonus) rewards.
      if (redeemModal.reward.rewardValueType === 'percent' && redeemForm.buyInAmount) {
        payload.buyInAmount = parseFloat(redeemForm.buyInAmount);
      }
      // Staff override of the one-per-day daily-exclusive limit (logged server-side).
      if (redeemForm.overrideDailyLimit) {
        payload.overrideDailyLimit = true;
      }
      await api.post(`/admin/users/${appUserId}/rewards/${redeemModal.reward.id}/redeem`, payload);
      setRedeemModal({ open: false, reward: null });
      setRedeemForm({ employeeNumber: '', pin: '', buyInAmount: '' });
      fetchUserRewards(); // Refresh rewards list
    } catch (err) {
      setRedeemError(err.response?.data?.error || 'Failed to redeem reward');
    } finally {
      setRedeemLoading(false);
    }
  };

  const fetchPlayerStats = useCallback(async () => {
    if (!tcPlayerId) {
      setError('No Player\'s Club account linked');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/admin/table-manager/player/${tcPlayerId}/stats`);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching player stats:', err);
      setError(err.response?.data?.error || 'Failed to load player stats');
    } finally {
      setLoading(false);
    }
  }, [tcPlayerId]);

  useEffect(() => {
    setVipAlert(null); // reset per-player toggle override when a new player loads
    if (visible && tcPlayerId) {
      fetchPlayerStats();
    } else if (visible && !tcPlayerId) {
      setStats(null);
      setError('No Player\'s Club account linked');
    }
    // Fetch app-feature data once an app account is known (either prop or derived from stats)
    if (visible && appUserId) {
      fetchPlayerAchievements();
      fetchCalculatedStatus();
      fetchTierOverride();
      fetchUserRewards();
    }
  }, [visible, tcPlayerId, fetchPlayerStats, appUserId, fetchPlayerAchievements, fetchCalculatedStatus, fetchTierOverride, fetchUserRewards]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatHours = (ticks) => {
    if (!ticks) return '0h';
    const hours = (ticks / 60).toFixed(1);
    return `${hours}h`;
  };

  if (!visible) return null;

  // Resolve display name/tier with fallbacks so the header renders when opened by playerId only.
  const headerTierDisplay = playerClubMember?.currentStatusDisplay || stats?.player?.tierDisplay;
  const headerName = (stats?.player?.firstName || stats?.player?.lastName)
    ? `${stats?.player?.firstName || ''} ${stats?.player?.lastName || ''}`.trim()
    : (player?.displayName || stats?.player?.PlayerName || '');

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-lg bg-gray-800 shadow-2xl z-50 overflow-hidden flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <User className="text-amber-500" size={20} />
            Player Details
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchPlayerStats}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-64">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-amber-500 mb-4"></div>
              <p className="text-gray-400">Loading player stats...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-64 px-6">
              <AlertCircle className="text-gray-500 mb-4" size={48} />
              <p className="text-gray-400 text-center">{error}</p>
              {!tcPlayerId && (
                <p className="text-gray-500 text-sm mt-2 text-center">
                  This user has not linked their Player's Club account yet.
                </p>
              )}
            </div>
          ) : stats ? (
            <div className="p-6 space-y-6">
              {/* Birthday Banner */}
              {stats.player?.isBirthday && (
                <div className="bg-amber-500/20 border border-amber-500/50 rounded-lg p-3 flex items-center justify-center gap-2">
                  <Cake className="text-amber-500" size={20} />
                  <span className="text-amber-500 font-semibold">Happy Birthday! {'\u{1F389}'}</span>
                </div>
              )}

              {/* Player Info Header */}
              <div className="flex items-start gap-4">
                <div
                  className={`w-16 h-16 flex-shrink-0 rounded-full flex items-center justify-center text-lg font-bold ${
                    stats.player?.isBirthday ? 'ring-2 ring-amber-500 ring-offset-2 ring-offset-gray-800' : ''
                  }`}
                  style={{ backgroundColor: getTierColorFromDisplay(headerTierDisplay) + '30' }}
                >
                  <span style={{ color: getTierColorFromDisplay(headerTierDisplay) }}>
                    {stats.player?.firstName?.charAt(0) || ''}{stats.player?.lastName?.charAt(0) || ''}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-white truncate">
                    {headerName}
                  </h3>
                  {stats.player?.alias && (
                    <p className="text-gray-400 italic">"{stats.player.alias}"</p>
                  )}
                  <p className="text-gray-500 text-sm flex items-center gap-1 mt-1">
                    <CreditCard size={14} />
                    Member #{stats.player?.memberNumber}
                  </p>
                  <div className="mt-2">
                    {headerTierDisplay ? (
                      <TierBadge
                        tier={headerTierDisplay}
                        color={getTierColorFromDisplay(headerTierDisplay)}
                      />
                    ) : (
                      <GuestBadge />
                    )}
                  </div>
                </div>
              </div>

              {/* Points Summary */}
              <div className="bg-gray-700/50 rounded-lg p-4">
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Points</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-amber-500">
                      {(stats.player?.pointsAvailable || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-400">Available</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-500">
                      {(stats.player?.pointsTotal || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-400">Lifetime</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-400">
                      {(stats.player?.pointsRedeemed || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-400">Redeemed</p>
                  </div>
                </div>
              </div>

              {/* XP & Status - New Section */}
              {appUserId && (
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Crown size={14} className="text-amber-500" />
                      XP & Status (App)
                    </span>
                    <button
                      onClick={handleRefreshStatus}
                      disabled={refreshing || calculatedLoading}
                      className="p-1 hover:bg-gray-600 rounded transition-colors disabled:opacity-50"
                      title="Refresh XP (recalculate)"
                    >
                      <RefreshCw size={14} className={`text-gray-400 ${refreshing ? 'animate-spin' : ''}`} />
                    </button>
                  </h4>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-amber-500">
                        {calculatedLoading ? '...' : (calculatedStatus?.calculated?.currentPeriodXp ?? playerClubMember?.currentPeriodXp ?? stats?.appUser?.currentPeriodXp ?? 0).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-400">Period XP</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-purple-400">
                        {calculatedLoading ? '...' : (calculatedStatus?.calculated?.lifetimeXp ?? playerClubMember?.lifetimeXp ?? stats?.appUser?.lifetimeXp ?? 0).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-400">Lifetime XP</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Current Status:</span>
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/20 text-amber-500">
                        {calculatedStatus?.calculated?.effectiveStatus?.displayName || playerClubMember?.currentStatusDisplay || stats?.appUser?.currentStatusDisplay || 'VIP I'}
                      </span>
                    </div>
                    {(playerClubMember?.lifetimeStatusTier ?? stats?.appUser?.lifetimeStatusTier) && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Lifetime Status:</span>
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400 flex items-center gap-1">
                          <Star size={10} />
                          {playerClubMember?.lifetimeStatusDisplay ?? stats?.appUser?.lifetimeStatusDisplay}
                        </span>
                      </div>
                    )}
                    {(playerClubMember?.statusValidThrough ?? stats?.appUser?.statusValidThrough) && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Valid Through:</span>
                        <span className="text-white">{formatDate(playerClubMember?.statusValidThrough ?? stats?.appUser?.statusValidThrough)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Casino Industry Flag */}
              {appUserId && playerClubMember && (
                <div className={`bg-gray-700/50 rounded-lg p-3 ${playerClubMember.isCasinoIndustry ? 'ring-1 ring-purple-500/50' : ''}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold uppercase tracking-wider ${playerClubMember.isCasinoIndustry ? 'text-purple-300' : 'text-gray-400'}`}>
                        {'\u{1F3E2}'} Casino Industry
                      </span>
                      {playerClubMember.isCasinoIndustry && (
                        <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-300 rounded text-[10px] font-bold">ACTIVE</span>
                      )}
                    </div>
                    <button
                      onClick={async () => {
                        const newValue = !playerClubMember.isCasinoIndustry;
                        const action = newValue ? 'flag as Casino Industry' : 'remove Casino Industry flag from';
                        if (!confirm(`${action} this player?`)) return;
                        try {
                          await api.put(`/status-system/admin/industry/${appUserId}`, { isCasinoIndustry: newValue });
                          toast.success(newValue ? 'Industry flag set' : 'Industry flag removed');
                          // Refresh calculated status to reflect the change
                          fetchCalculatedStatus();
                        } catch (err) {
                          toast.error(err.response?.data?.error || 'Failed to toggle industry flag');
                        }
                      }}
                      className={`text-xs px-2 py-1 rounded ${
                        playerClubMember.isCasinoIndustry
                          ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                          : 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
                      }`}
                    >
                      {playerClubMember.isCasinoIndustry ? 'Remove' : 'Enable'}
                    </button>
                  </div>
                  {playerClubMember.tcPlayerRole != null && (
                    <p className="text-[10px] text-gray-500 mt-1">TC Role: {playerClubMember.tcPlayerRole}</p>
                  )}
                </div>
              )}

              {/* VIP Arrival Notification */}
              {appUserId && (() => {
                const vipOn = vipAlert !== null ? vipAlert : (stats.appUser?.vipArrivalAlert || false);
                return (
                  <div className={`bg-gray-700/50 rounded-lg p-3 ${vipOn ? 'ring-1 ring-amber-500/50' : ''}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-semibold uppercase tracking-wider ${vipOn ? 'text-amber-300' : 'text-gray-400'}`}>
                          {'⭐'} VIP Arrival Alerts
                        </span>
                        {vipOn && (
                          <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-300 rounded text-[10px] font-bold">ON</span>
                        )}
                      </div>
                      <button
                        onClick={async () => {
                          const newValue = !vipOn;
                          try {
                            await api.put(`/status-system/admin/vip-arrival/${appUserId}`, { vipArrivalAlert: newValue });
                            setVipAlert(newValue);
                            toast.success(newValue ? 'VIP arrival alerts enabled' : 'VIP arrival alerts disabled');
                          } catch (err) {
                            toast.error(err.response?.data?.error || 'Failed to update VIP arrival alerts');
                          }
                        }}
                        className={`text-xs px-2 py-1 rounded ${
                          vipOn
                            ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                            : 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30'
                        }`}
                      >
                        {vipOn ? 'Disable' : 'Enable'}
                      </button>
                    </div>
                    <p className="text-[10px] text-gray-500 mt-1">
                      Pushes configured staff when this player arrives at the casino or is seated at a table.
                    </p>
                  </div>
                );
              })()}

              {/* Tier Override Section */}
              {appUserId && (
                <div className={`bg-gray-700/50 rounded-lg p-4 ${tierOverride?.hasOverride ? 'ring-1 ring-orange-500/50' : ''}`}>
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Crown size={14} className={tierOverride?.hasOverride ? 'text-orange-500' : 'text-gray-500'} />
                      Tier Override
                      {tierOverride?.hasOverride && (
                        <span className="px-1.5 py-0.5 bg-orange-500/20 text-orange-400 rounded text-[10px] font-bold">ACTIVE</span>
                      )}
                    </span>
                    {!showOverrideForm && (
                      <button
                        onClick={() => {
                          setShowOverrideForm(true);
                          if (tierOverride?.hasOverride) {
                            setOverrideForm({
                              tier: tierOverride.override.tier,
                              level: String(tierOverride.override.level),
                              reason: tierOverride.override.reason || '',
                            });
                          }
                        }}
                        className="text-xs text-amber-500 hover:text-amber-400"
                      >
                        {tierOverride?.hasOverride ? 'Edit' : '+ Set Override'}
                      </button>
                    )}
                  </h4>

                  {tierOverrideLoading ? (
                    <div className="flex justify-center py-3">
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-amber-500" />
                    </div>
                  ) : tierOverride?.hasOverride && !showOverrideForm ? (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Override Tier:</span>
                        <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-orange-500/20 text-orange-400">
                          {tierOverride.override.displayName}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Expires:</span>
                        <span className="text-white">{formatDate(tierOverride.override.until)}</span>
                      </div>
                      {tierOverride.override.reason && (
                        <div className="flex justify-between">
                          <span className="text-gray-400">Reason:</span>
                          <span className="text-white text-right max-w-[60%]">{tierOverride.override.reason}</span>
                        </div>
                      )}
                      {tierOverride.override.setByName && (
                        <div className="flex justify-between">
                          <span className="text-gray-400">Set By:</span>
                          <span className="text-white">{tierOverride.override.setByName}</span>
                        </div>
                      )}
                      <button
                        onClick={handleClearOverride}
                        disabled={overrideSaving}
                        className="w-full mt-2 px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 text-xs disabled:opacity-50"
                      >
                        {overrideSaving ? 'Clearing...' : 'Clear Override'}
                      </button>
                    </div>
                  ) : showOverrideForm ? (
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">Tier</label>
                        <select
                          value={overrideForm.tier}
                          onChange={(e) => {
                            const newTier = e.target.value;
                            setOverrideForm({ ...overrideForm, tier: newTier, level: '' });
                          }}
                          className="w-full bg-gray-800 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
                        >
                          <option value="">Select tier...</option>
                          {tierConfig?.tiers?.map(t => (
                            <option key={t.key} value={t.key}>{t.baseName || t.key}</option>
                          ))}
                        </select>
                      </div>
                      {overrideForm.tier && (
                        <div>
                          <label className="text-xs text-gray-400 block mb-1">Level</label>
                          <select
                            value={overrideForm.level}
                            onChange={(e) => setOverrideForm({ ...overrideForm, level: e.target.value })}
                            className="w-full bg-gray-800 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
                          >
                            <option value="">Select level...</option>
                            {getAvailableLevels(overrideForm.tier).map(l => (
                              <option key={l.level} value={l.level}>{l.displayName} (Level {l.level})</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">Reason (optional)</label>
                        <input
                          type="text"
                          value={overrideForm.reason}
                          onChange={(e) => setOverrideForm({ ...overrideForm, reason: e.target.value })}
                          placeholder="e.g., Comp upgrade, VIP event"
                          className="w-full bg-gray-800 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                      <p className="text-xs text-gray-500">
                        Override expires at end of current period. Player sees the override tier in the app and receives its benefits.
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => { setShowOverrideForm(false); setOverrideForm({ tier: '', level: '', reason: '' }); }}
                          className="flex-1 px-3 py-1.5 bg-gray-600 text-gray-300 rounded-lg hover:bg-gray-500 text-xs"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSetOverride}
                          disabled={!overrideForm.tier || !overrideForm.level || overrideSaving}
                          className="flex-1 px-3 py-1.5 bg-amber-500 text-black rounded-lg hover:bg-amber-400 text-xs font-medium disabled:opacity-50"
                        >
                          {overrideSaving ? 'Saving...' : tierOverride?.hasOverride ? 'Update Override' : 'Set Override'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-xs">No active override. Player using earned tier.</p>
                  )}

                  {tierOverride?.expiredOverride && !tierOverride?.hasOverride && (
                    <div className="mt-2 p-2 bg-gray-800/50 rounded text-xs text-gray-500">
                      Previous override ({tierOverride.expiredOverride.displayName}) expired {formatDate(tierOverride.expiredOverride.expiredAt)}
                      {tierOverride.expiredOverride.reason && ` — ${tierOverride.expiredOverride.reason}`}
                    </div>
                  )}
                </div>
              )}

              {/* Achievements Section */}
              {achievements?.success && (
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Award size={14} className="text-green-500" />
                    Achievements
                  </h4>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-400">Progress</span>
                      <span className="text-white font-medium">
                        {achievements.summary?.unlockedCount || 0} / {achievements.summary?.totalAchievements || 0}
                      </span>
                    </div>
                    <div className="w-full h-2 bg-gray-600 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-green-500 to-emerald-400 rounded-full transition-all"
                        style={{ width: `${achievements.summary?.completionPercent || 0}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs mt-1">
                      <span className="text-green-400">+{achievements.summary?.totalXpEarned?.toLocaleString() || 0} XP earned</span>
                      <span className="text-gray-500">{achievements.summary?.completionPercent || 0}%</span>
                    </div>
                  </div>

                  {/* Recent Unlocked Achievements */}
                  {achievements.achievements?.filter(a => a.unlocked).length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-500 mb-2">Recently Unlocked:</p>
                      {achievements.achievements
                        .filter(a => a.unlocked)
                        .slice(0, 5)
                        .map(achievement => (
                          <div
                            key={achievement.id}
                            className="flex items-center gap-2 bg-green-900/20 border border-green-700/30 rounded-lg p-2"
                          >
                            <span className="text-lg">{getAchievementIcon(achievement.icon)}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-sm font-medium truncate">{achievement.name}</p>
                              <p className="text-gray-400 text-xs truncate">{achievement.description}</p>
                            </div>
                            <span className="text-amber-500 text-xs font-bold">+{achievement.xp}</span>
                          </div>
                        ))}
                    </div>
                  )}

                  {/* Locked Preview */}
                  {achievements.achievements?.filter(a => !a.unlocked).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-600">
                      <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                        <Lock size={12} />
                        Next to unlock:
                      </p>
                      {achievements.achievements
                        .filter(a => !a.unlocked)
                        .slice(0, 2)
                        .map(achievement => (
                          <div
                            key={achievement.id}
                            className="flex items-center gap-2 opacity-60 p-2"
                          >
                            <span className="text-lg grayscale">{getAchievementIcon(achievement.icon)}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-gray-400 text-sm truncate">{achievement.name}</p>
                            </div>
                            <span className="text-gray-500 text-xs">+{achievement.xp}</span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}

              {achievementsLoading && (
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-gray-400">
                    <RefreshCw size={14} className="animate-spin" />
                    <span className="text-sm">Loading achievements...</span>
                  </div>
                </div>
              )}

              {/* Play Statistics */}
              {stats.stats && (
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <TrendingUp size={14} />
                    Play Statistics (All Time)
                  </h4>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center">
                      <p className="text-xl font-bold text-white">{stats.stats.totalSessions || 0}</p>
                      <p className="text-xs text-gray-400">Sessions</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold text-white">{formatHours(stats.stats.totalTicks)}</p>
                      <p className="text-xs text-gray-400">Hours Played</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold text-white">
                        {(stats.stats.totalPointsEarned || 0).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-400">Points Earned</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    {stats.stats.averageBet != null && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Avg Wager:</span>
                        <span className="text-white">${Number(stats.stats.averageBet).toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-400">First Session:</span>
                      <span className="text-white">{formatBusinessDate(stats.stats.firstSession)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Last Session:</span>
                      <span className="text-white">{formatBusinessDate(stats.stats.lastSession)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Favorite Games */}
              {stats.favoriteGames && stats.favoriteGames.length > 0 && (
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Gamepad2 size={14} />
                    Favorite Games
                  </h4>
                  <div className="space-y-2">
                    {stats.favoriteGames.slice(0, 5).map((game, index) => (
                      <div key={game.Game_Name} className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-500 text-xs font-bold flex items-center justify-center">
                          #{index + 1}
                        </span>
                        <span className="flex-1 text-white text-sm">{game.Game_Name}</span>
                        <span className="text-gray-400 text-xs">{game.sessionCount} sessions</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* App Account Status */}
              <div className="bg-gray-700/50 rounded-lg p-4">
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">App Account</h4>
                {stats.appUser ? (
                  <div className="flex items-center gap-2 text-green-400">
                    <Link2 size={16} />
                    <span>Linked to app account</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Link2Off size={16} />
                    <span>Not linked to app</span>
                  </div>
                )}
              </div>

              {/* Contact & Details - show if there's data OR the caller is restricted (so the lock note appears) */}
              {(stats.player?.email || stats.appUser?.email || stats.player?.phone || stats.appUser?.phone || stats.player?.birthday || stats.player?.address || stats.player?.memberSince || !canContact || !canIdentity) && (
              <div className="bg-gray-700/50 rounded-lg p-4">
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Contact & Details</h4>
                <div className="space-y-3">
                  {(stats.player?.email || stats.appUser?.email) && (
                    <div className="flex items-center gap-3 text-sm">
                      <Mail size={14} className="text-gray-400" />
                      <span className="text-white">{stats.player?.email || stats.appUser?.email}</span>
                    </div>
                  )}
                  {(stats.player?.phone || stats.appUser?.phone) && (
                    <div className="flex items-center gap-3 text-sm">
                      <Phone size={14} className="text-gray-400" />
                      <span className="text-white">{stats.player?.phone || stats.appUser?.phone}</span>
                    </div>
                  )}
                  {stats.player?.birthday && (
                    <div className="flex items-center gap-3 text-sm">
                      <Gift size={14} className={stats.player.isBirthday ? 'text-amber-500' : 'text-gray-400'} />
                      <span className={stats.player.isBirthday ? 'text-amber-500 font-semibold' : 'text-white'}>
                        Birthday: {stats.player.birthday}
                      </span>
                    </div>
                  )}
                  {stats.player?.address && (
                    <div className="flex items-center gap-3 text-sm">
                      <MapPin size={14} className="text-gray-400" />
                      <span className="text-white">{stats.player.address}</span>
                    </div>
                  )}
                  {stats.player?.memberSince && (
                  <div className="flex items-center gap-3 text-sm">
                    <Calendar size={14} className="text-gray-400" />
                    <span className="text-white">Member since {formatDate(stats.player?.memberSince)}</span>
                  </div>
                  )}
                  {(!canContact || !canIdentity) && (
                    <div className="flex items-center gap-2 text-xs text-gray-500 pt-1">
                      <Lock size={12} />
                      <span>
                        {!canContact && !canIdentity
                          ? 'Contact & identity details are limited by your permissions'
                          : !canContact
                            ? 'Contact details are limited by your permissions'
                            : 'Identity details are limited by your permissions'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Staff Notes */}
              {stats.player?.notes && (
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <FileText size={14} />
                    Staff Notes
                  </h4>
                  <div className="bg-gray-800 rounded-lg p-3 border-l-2 border-amber-500">
                    <p className="text-gray-300 text-sm">{stats.player.notes}</p>
                  </div>
                </div>
              )}

              {/* Preferred Games */}
              {stats.player?.preferredGames && (
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Preferred Games</h4>
                  <p className="text-white text-sm">{stats.player.preferredGames}</p>
                </div>
              )}

              {/* Reward Preferences */}
              {stats.rewardPreferences && (stats.rewardPreferences.freePlayRank || stats.rewardPreferences.foodBeverageRank || stats.rewardPreferences.merchandiseRank) && (
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Award size={14} />
                    Reward Preferences
                  </h4>
                  <div className="space-y-2">
                    {[1, 2, 3].map((rank) => {
                      const prefs = stats.rewardPreferences;
                      let label = '';
                      let icon = '';
                      if (prefs.freePlayRank === rank) { label = 'Free Play'; icon = '\u{1F3B0}'; }
                      else if (prefs.foodBeverageRank === rank) { label = 'Food & Beverage'; icon = '\u{1F37D}\u{FE0F}'; }
                      else if (prefs.merchandiseRank === rank) { label = 'Merchandise'; icon = '\u{1F6CD}\u{FE0F}'; }

                      if (!label) return null;
                      return (
                        <div key={rank} className="flex items-center gap-3 bg-gray-600/50 rounded-lg p-2">
                          <span className="w-6 h-6 rounded-full bg-yellow-600 text-gray-900 text-xs font-bold flex items-center justify-center">
                            {rank}
                          </span>
                          <span className="text-lg">{icon}</span>
                          <span className="text-white text-sm font-medium">{label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Rewards Section */}
              {appUserId && (
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Gift size={14} />
                    Rewards
                    {userRewards && (
                      <span className="ml-auto text-xs text-gray-500 font-normal">
                        {userRewards.rewards?.filter(r => r.canRedeem !== false && !r.dailyExclusiveLocked).length || 0} available {'·'} {userRewards.totalRedemptions || 0} redeemed
                      </span>
                    )}
                  </h4>

                  {rewardsLoading ? (
                    <div className="flex items-center gap-2 text-gray-400">
                      <RefreshCw size={14} className="animate-spin" />
                      <span className="text-sm">Loading rewards...</span>
                    </div>
                  ) : userRewards ? (
                    <>
                      {/* Sub-tabs */}
                      <div className="flex gap-1 mb-3 bg-gray-800 rounded-lg p-1">
                        <button
                          onClick={() => setRewardsTab('available')}
                          className={`flex-1 text-xs py-1.5 rounded-md font-medium transition-colors ${rewardsTab === 'available' ? 'bg-amber-500 text-gray-900' : 'text-gray-400 hover:text-white'}`}
                        >
                          Available ({userRewards.rewards?.filter(r => r.canRedeem !== false && !r.dailyExclusiveLocked).length || 0})
                        </button>
                        <button
                          onClick={() => setRewardsTab('history')}
                          className={`flex-1 text-xs py-1.5 rounded-md font-medium transition-colors ${rewardsTab === 'history' ? 'bg-amber-500 text-gray-900' : 'text-gray-400 hover:text-white'}`}
                        >
                          History ({userRewards.totalRedemptions || 0})
                        </button>
                      </div>

                      {/* Available Rewards */}
                      {rewardsTab === 'available' && (
                        <div className="space-y-2 max-h-72 overflow-y-auto">
                          {userRewards.rewards?.filter(r => r.canRedeem !== false).length > 0 ? userRewards.rewards.filter(r => r.canRedeem !== false).map((reward) => (
                            <div key={reward.id} className={`flex items-start gap-3 p-2.5 rounded-lg ${reward.dailyExclusiveLocked ? 'bg-gray-800/40 opacity-60' : 'bg-gray-800/60'}`}>
                              <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-base flex-shrink-0">
                                {getAchievementIcon(reward.icon) || '\u{1F381}'}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="text-sm font-medium text-white truncate">{reward.title}</p>
                                  {reward.rewardValueType === 'dollar' && reward.rewardValue && (
                                    <span className="text-xs text-green-400 font-semibold">${reward.rewardValue}</span>
                                  )}
                                  {reward.rewardValueType === 'percent' && reward.rewardValue && (
                                    <span className="text-xs text-green-400 font-semibold">{reward.rewardValue}% buy-in bonus</span>
                                  )}
                                  {reward.requiresStaffAuth && (
                                    <span className="text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30 px-1.5 py-0.5 rounded">Staff Auth</span>
                                  )}
                                  {reward.dailyExclusive && (
                                    <span className="text-xs bg-amber-500/20 text-amber-400 border border-amber-500/30 px-1.5 py-0.5 rounded" title="One reward with this toggle can be redeemed per day">1/day</span>
                                  )}
                                  {reward.isActive === false && (
                                    <span className="text-xs bg-red-500/20 text-red-400 border border-red-500/30 px-1.5 py-0.5 rounded">Inactive</span>
                                  )}
                                  {reward.isHidden === true && (
                                    <span className="text-xs bg-gray-600/50 text-gray-400 border border-gray-500/30 px-1.5 py-0.5 rounded">Hidden</span>
                                  )}
                                </div>
                                {reward.description && (
                                  <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{reward.description}</p>
                                )}
                                <div className="flex items-center gap-3 mt-1 flex-wrap">
                                  {reward.redemptionCount > 0 && (
                                    <span className="text-xs text-amber-400">Redeemed {reward.redemptionCount}&times;</span>
                                  )}
                                  {reward.visibleUntil && (
                                    <span className="text-xs text-gray-500">Expires {new Date(reward.visibleUntil).toLocaleDateString()}</span>
                                  )}
                                  {reward.assignmentType && reward.assignmentType !== 'all' && (
                                    <span className="text-xs text-purple-400 capitalize">{reward.assignmentType === 'user' ? 'Personally assigned' : reward.assignmentType}</span>
                                  )}
                                  {reward.dailyExclusiveLocked && (
                                    <span className="text-xs text-amber-400">Daily limit reached &middot; available tomorrow</span>
                                  )}
                                </div>
                              </div>
                              {reward.dailyExclusiveLocked ? (
                                <button
                                  onClick={() => {
                                    setRedeemError('');
                                    setRedeemForm({ employeeNumber: '', pin: '', buyInAmount: '', overrideDailyLimit: true });
                                    setRedeemModal({ open: true, reward });
                                  }}
                                  title="Daily limit reached - redeem anyway with a staff override"
                                  className="flex-shrink-0 px-2.5 py-1.5 bg-amber-600 hover:bg-amber-500 text-white text-xs font-medium rounded-md transition-colors"
                                >
                                  Override
                                </button>
                              ) : (
                                <button
                                  onClick={() => {
                                    setRedeemError('');
                                    setRedeemForm({ employeeNumber: '', pin: '', buyInAmount: '' });
                                    setRedeemModal({ open: true, reward });
                                  }}
                                  className="flex-shrink-0 px-2.5 py-1.5 bg-green-600 hover:bg-green-500 text-white text-xs font-medium rounded-md transition-colors"
                                >
                                  Redeem
                                </button>
                              )}
                            </div>
                          )) : (
                            <p className="text-sm text-gray-500 text-center py-4">No rewards available for this user</p>
                          )}
                        </div>
                      )}

                      {/* Redemption History */}
                      {rewardsTab === 'history' && (
                        <div className="space-y-2 max-h-72 overflow-y-auto">
                          {userRewards.redemptions?.length > 0 ? userRewards.redemptions.map((redemption) => (
                            <div key={redemption.id} className="flex items-start gap-3 p-2.5 bg-gray-800/60 rounded-lg">
                              <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-base flex-shrink-0">
                                {getAchievementIcon(redemption.reward?.icon) || '✅'}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-white truncate">
                                  {redemption.reward?.title || redemption.rewardTitle || 'Unknown Reward'}
                                </p>
                                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                  <span className="text-xs text-gray-400">
                                    {new Date(redemption.redeemedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                  </span>
                                  {redemption.redemptionMethod && (
                                    <span className={`text-xs px-1.5 py-0.5 rounded border ${
                                      redemption.redemptionMethod === 'staff_auth'
                                        ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                                        : redemption.redemptionMethod === 'auto'
                                        ? 'bg-purple-500/20 text-purple-400 border-purple-500/30'
                                        : 'bg-gray-600/50 text-gray-400 border-gray-500/30'
                                    }`}>
                                      {redemption.redemptionMethod === 'staff_auth' ? 'Staff Auth' : redemption.redemptionMethod === 'auto' ? 'Auto' : 'Self-Service'}
                                    </span>
                                  )}
                                </div>
                                {(redemption.redeemedByStaffName || redemption.staffName) && (
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    Staff: {redemption.redeemedByStaffName || redemption.staffName}
                                    {(redemption.redeemedByStaffNumber || redemption.staffNumber) && ` (#${redemption.redeemedByStaffNumber || redemption.staffNumber})`}
                                  </p>
                                )}
                              </div>
                            </div>
                          )) : (
                            <p className="text-sm text-gray-500 text-center py-4">No redemptions yet</p>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">Unable to load rewards</p>
                  )}

                  {/* Staff PIN Redemption Modal */}
                  {redeemModal.open && (
                    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setRedeemModal({ open: false, reward: null })}>
                      <div className="bg-gray-800 rounded-xl p-5 w-80 shadow-2xl border border-gray-700" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-white font-semibold text-sm">Staff PIN Required</h3>
                          <button onClick={() => setRedeemModal({ open: false, reward: null })} className="text-gray-400 hover:text-white">
                            <X size={16} />
                          </button>
                        </div>

                        <div className="bg-gray-700/50 rounded-lg p-3 mb-4">
                          <p className="text-xs text-gray-400">Redeeming reward for this player:</p>
                          <p className="text-sm text-white font-medium mt-1">{redeemModal.reward?.title}</p>
                          {redeemModal.reward?.rewardValue && (
                            <p className="text-xs text-green-400 font-semibold mt-0.5">
                              {redeemModal.reward.rewardValueType === 'dollar' ? '$' : ''}{redeemModal.reward.rewardValue}{redeemModal.reward.rewardValueType === 'percent' ? '%' : ''}
                              {redeemModal.reward.rewardValueType === 'percent' ? ' buy-in bonus' : ''}
                            </p>
                          )}
                        </div>

                        {redeemModal.reward?.dailyExclusive && !redeemModal.reward?.dailyExclusiveLocked && (
                          <div className="mb-4 p-2.5 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                            <p className="text-xs text-amber-300">
                              Heads up: only one reward with the daily toggle can be redeemed per day (resets 6 AM Pacific). Redeeming this will lock the player&apos;s other daily rewards until tomorrow. Confirm to proceed.
                            </p>
                          </div>
                        )}

                        {redeemModal.reward?.dailyExclusiveLocked && (
                          <div className="mb-4 p-2.5 bg-amber-600/15 border border-amber-500/40 rounded-lg">
                            <label className="flex items-start gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!redeemForm.overrideDailyLimit}
                                onChange={e => setRedeemForm(f => ({ ...f, overrideDailyLimit: e.target.checked }))}
                                className="mt-0.5"
                              />
                              <span className="text-xs text-amber-300">
                                This player has already used their one-per-day reward today. Override the daily limit and record this redemption anyway (staff correction / comp). This override is logged.
                              </span>
                            </label>
                          </div>
                        )}

                        <div className="space-y-3">
                          {redeemModal.reward?.rewardValueType === 'percent' && (
                            <div>
                              <label className="text-xs text-gray-400 block mb-1">Initial Buy-In Amount ($)</label>
                              <input
                                type="number"
                                min="0"
                                step="1"
                                value={redeemForm.buyInAmount}
                                onChange={e => setRedeemForm(f => ({ ...f, buyInAmount: e.target.value }))}
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
                                placeholder="e.g. 100"
                              />
                              {redeemForm.buyInAmount && !isNaN(parseFloat(redeemForm.buyInAmount)) && redeemModal.reward?.rewardValue && (
                                <p className="text-xs text-green-400 mt-1">
                                  Promo value: ${(parseFloat(redeemForm.buyInAmount) * parseFloat(redeemModal.reward.rewardValue) / 100).toFixed(2)}
                                  {' '}({redeemModal.reward.rewardValue}% of ${parseFloat(redeemForm.buyInAmount).toFixed(2)})
                                </p>
                              )}
                            </div>
                          )}
                          <div>
                            <label className="text-xs text-gray-400 block mb-1">Employee Number</label>
                            <input
                              type="text"
                              value={redeemForm.employeeNumber}
                              onChange={e => setRedeemForm(f => ({ ...f, employeeNumber: e.target.value }))}
                              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
                              placeholder="Enter employee number"
                              autoFocus
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-400 block mb-1">PIN</label>
                            <input
                              type="password"
                              value={redeemForm.pin}
                              onChange={e => setRedeemForm(f => ({ ...f, pin: e.target.value }))}
                              onKeyDown={e => e.key === 'Enter' && handleRedeemReward()}
                              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
                              placeholder="Enter PIN"
                            />
                          </div>
                        </div>

                        {redeemError && (
                          <div className="mt-3 p-2 bg-red-500/20 border border-red-500/30 rounded-lg">
                            <p className="text-xs text-red-400">{redeemError}</p>
                          </div>
                        )}

                        <div className="flex gap-2 mt-4">
                          <button
                            onClick={() => setRedeemModal({ open: false, reward: null })}
                            className="flex-1 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm rounded-lg transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleRedeemReward}
                            disabled={redeemLoading || !redeemForm.employeeNumber || !redeemForm.pin || (redeemModal.reward?.dailyExclusiveLocked && !redeemForm.overrideDailyLimit)}
                            className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-500 disabled:bg-gray-600 disabled:text-gray-400 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                          >
                            {redeemLoading ? (
                              <>
                                <RefreshCw size={14} className="animate-spin" />
                                Redeeming...
                              </>
                            ) : (
                              'Confirm Redemption'
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Recent Activity */}
              {stats.recentActivity && stats.recentActivity.length > 0 && (
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Clock size={14} />
                    Recent Activity
                  </h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {stats.recentActivity.slice(0, 10).map((activity) => (
                      <div key={activity.Id} className="flex items-center justify-between text-sm py-2 border-b border-gray-600 last:border-0">
                        <div>
                          <p className="text-white">{activity.Game_Name}</p>
                          <p className="text-gray-400 text-xs">Table {activity.Table_Name}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-amber-500 font-medium">+{activity.Total_Points} pts</p>
                          <p className="text-gray-500 text-xs">{formatBusinessDate(activity.Open_Date)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.3s ease-out forwards;
        }
      `}</style>
    </>
  );
};

export default PlayerDetailModal;
